import { useEffect, useRef } from "react";
import posthog from "posthog-js";
import type { Couple, Partner } from "../types";
import { COUPLE_EVENTS, type CoupleEventName, type CoupleEventProperties } from "./events";

/**
 * The only way /me code sends an analytics event. A no-op until PostHog is
 * initialised (no key in local dev and tests), and it can never throw into a
 * user flow.
 */
export function track<E extends CoupleEventName>(event: E, properties: CoupleEventProperties[E]): void {
  if (!posthog.__loaded) return;
  try {
    posthog.capture(event, properties);
  } catch {
    // Analytics must never break the product.
  }
}

/** Fires `track` once per mount (StrictMode-safe) when `ready` first turns true. */
export function useTrackOnce<E extends CoupleEventName>(
  event: E,
  properties: CoupleEventProperties[E],
  ready = true,
): void {
  const sent = useRef(false);
  const latest = useRef(properties);
  latest.current = properties;
  useEffect(() => {
    if (!ready || sent.current) return;
    sent.current = true;
    track(event, latest.current);
  }, [event, ready]);
}

/**
 * Called on an identity SWITCH (logout, a different person's link). The
 * couple→person upgrade is the same human and must not reset, or the
 * pre-bind onboarding steps detach from the person.
 */
export function resetAnalytics(): void {
  if (!posthog.__loaded) return;
  posthog.reset();
}

const INVITE_OPEN_PENDING_KEY = "me_invite_open_pending";

/**
 * `MeAuthLanding` redeems the invite link and navigates away before
 * `MeShell` has loaded the couple, so `couple_invite_opened` can't carry its
 * join keys yet if captured there. Queue it here; `useMeAnalyticsIdentity`
 * fires it once the super properties it rides on are registered.
 */
export function markInviteOpenPending(): void {
  try {
    sessionStorage.setItem(INVITE_OPEN_PENDING_KEY, "1");
  } catch {
    // Analytics must never break the product.
  }
}

function consumeInviteOpenPending(): boolean {
  try {
    if (sessionStorage.getItem(INVITE_OPEN_PENDING_KEY) !== "1") return false;
    sessionStorage.removeItem(INVITE_OPEN_PENDING_KEY);
    return true;
  } catch {
    return false;
  }
}

/**
 * Identifies the acting partner by `person_global_uuid` (the same distinct id
 * the API and the legacy app use) and registers the join keys from §4.1 as
 * super properties. Pre-bind there is no partner yet, so only the couple is
 * registered; identify() then merges the anonymous steps into the person.
 */
export function useMeAnalyticsIdentity(couple: Couple | null, partner: Partner | null): void {
  const coupleId = couple?.id ?? "";
  const therapistId = couple?.therapistId ?? "";
  const partnerId = partner?.id ?? "";

  useEffect(() => {
    if (!posthog.__loaded || !coupleId) return;
    if (partnerId && posthog.get_distinct_id() !== partnerId) {
      posthog.identify(partnerId);
    }
    posthog.register({
      app: "homebase",
      couple_id: coupleId,
      ...(therapistId ? { therapist_id: therapistId } : {}),
    });
    // Fire any queued invite-open capture now that couple_id/therapist_id
    // are registered as super properties, so the event carries its join keys.
    if (consumeInviteOpenPending()) {
      track(COUPLE_EVENTS.inviteOpened, {});
    }
  }, [coupleId, therapistId, partnerId]);
}
